import * as long from 'long';
import BaseBehavior from '../../../framework/base/base';
import {
	hasStorage,
	setValueWithExpiry,
	getValueWithExpiry
} from '../../../shared/utilities/storage';

// todo add expiry time to local storage and cookie

export default class DynamicData extends BaseBehavior {
	constructor(element) {
		super(element);
		this.lastReceivedMessageId = '';
		this.messageIDArray = [];
		this.accessToken = '';
		this.reconnectionTimer = null;
		this.priceSubscriptionUrl = this.element.dataset.subsUrl;
		this.webSocketConnectionUrl = this.element.dataset.wsUrl;
		this.authTokenUrl = this.element.dataset.authTokenUrl;
		this.historicalChange = this.element.dataset.changefrequency;
		this.tableId = this.element.dataset.instance;
		this.configId = this.element.dataset.configid;
		this.cacheExpiration = 19 * 60 * 1000; // 19 min time to expire sso token
		this.storage = hasStorage() ? 'local' : 'cookie';
		this.changeCategories = {
			OneDayReturn: 'PercentChangeDaily',
			FiveYearsReturn: 'PercentChange5Years',
			OneMonthReturn: 'PercentChange1Month',
			OneWeekReturn: 'PercentChangeWeekly',
			OneYearReturn: 'PercentChange1Year'
		};
		this.subscriptions = {}; // object type: { assetType: { uics: [], refId: null } }
		this.setSubscriptions();
		this.getAccessToken();
	}

	setSubscriptions() {
		Array.from(this.element.querySelectorAll('tr[data-uic]')).forEach(tr => {
			const { assetType, uic } = tr.dataset;
			if (this.subscriptions[assetType]) {
				this.subscriptions[assetType].uics.push(uic);
			} else {
				this.subscriptions[assetType] = {
					uics: [uic],
					refId: null,
					Amount: 1,
					AmountType: 'Quantity'
				};
			}
		});
	}

	onSuccessTokenRequest(text, source = null) {
		if (source !== 'cache') {
			this.accessToken = text.replace(/"/g, ''); // response has quotation, we can get response without quotes from server
			setValueWithExpiry(
				'apiToken' + this.tableId,
				this.accessToken,
				this.storage,
				this.cacheExpiration
			);
		} else {
			this.accessToken = text;
		}
		this.handleConnection();
		this.handleSubscribePrices();
	}

	getAccessToken() {
		const token = getValueWithExpiry('apiToken' + this.tableId, this.storage);
		if (this.configId) {
			this.authTokenUrl = this.authTokenUrl + '?configId=' + this.configId;
		}
		if (token) {
			this.onSuccessTokenRequest(token, 'cache');
		} else {
			this.createRequest({
				url: this.authTokenUrl,
				method: 'GET',
				onSuccess: this.onSuccessTokenRequest.bind(this)
			});
		}
	}

	handleConnection() {
		this.contextId = crypto.randomUUID();

		if (this.accessToken) {
			const auth = encodeURIComponent('BEARER ' + this.accessToken);
			let connectionUrl = `${this.webSocketConnectionUrl}?contextId=${this.contextId}&Authorization=${auth}`;

			if (this.messageIDArray.length > 0) {
				connectionUrl += `&messageid=${this.lastReceivedMessageId}`;
			}

			const connection = new WebSocket(connectionUrl);

			connection.binaryType = 'arraybuffer';

			connection.onerror = error => {
				// an error occurred when sending/receiving data
				// eslint-disable-next-line no-console
				console.error(error);
			};

			connection.onmessage = this.onSocketMessage.bind(this);
			connection.onclose = this.onSocketClose.bind(this);

			this.connection = connection;
		} else {
			this.getAccessToken();
		}
	}

	onSocketMessage(message) {
		if (message.data instanceof ArrayBuffer) {
			const messageResults = this.parseMessages(message.data);

			messageResults.forEach(messageResult => {
				if (this.isControlMessage(messageResult)) {
					this.checkControlMessages(messageResult);
				} else {
					this.messageIDArray.push(messageResult.messageId);
					this.lastReceivedMessageId = messageResult.messageId;
				}
			});
		} else if (typeof message.data === 'string') {
			this.handleDisconnection(1000, 'Policy Violated .GoodBye!');
		}
	}

	onSocketClose(event) {
		if (event.code !== 1001) {
			this.messageIDArray = [];
		}
	}

	isControlMessage(parsedMessage) {
		return parsedMessage.referenceId.startsWith('_');
	}

	checkControlMessages(parsedMessage) {
		switch (parsedMessage.referenceId) {
			case '_resetsubscriptions':
				// Server is not able to send messages and client needs to reset subscriptions by recreating them
				// reset subscription
				this.resetSubscription(parsedMessage);
				break;
			case '_disconnect':
				// The server has disconnected the client.This messages requires you to reauthenticate
				// if you wish to continue receiving messages.We stop the websocket here.
				this.handleDisconnection(1000, 'Server Disconnected');
				break;
			default:
				break;
		}
	}

	resetSubscription(controlMessage) {
		const payload = JSON.parse(controlMessage.payload);
		const { TargetReferenceIds } = payload;
		// Delete the subscriptions the server tells us need to be reconnected.
		// And resubscribe once deleted successfully;
		if (TargetReferenceIds && TargetReferenceIds.length) {
			TargetReferenceIds.forEach(refId => {
				this.deleteSubscription(refId);
			});
		} else {
			// delete all subscriptions
			Object.keys(this.subscriptions).forEach(assetType => {
				this.deleteSubscription(this.subscriptions[assetType].refId);
			});
		}
	}

	deleteSubscription(refId) {
		const onSuccess = () => {
			this.subscribeInfoPrice(refId);
		};

		const headers = {
			Authorization: 'BEARER ' + this.accessToken
		};
		const url = this.priceSubscriptionUrl + '/' + this.contextId + '/' + refId;

		this.createRequest({ url, method: 'DELETE', onSuccess, headers });
	}

	/**
	 * This function creates subscription for one assetType
	 * refId is null in case of initial request
	 * refId will have value when it's a reset request and previous subscription is deleted
	 * in reset request we can make use of old refId
	 *
	 */
	subscribeInfoPrice(refId, assetType) {
		let refIdLocal = refId;
		let assetTypeLocal = assetType;
		if (!refIdLocal) {
			refIdLocal = crypto.randomUUID() + '_' + Number.parseInt(Math.random() * 100, 10);
			// set refId in local subscription object
			this.subscriptions[assetType].refId = refIdLocal;
		} else {
			// this subscription refId is deleted and now we need new subsription request
			// get AssetType
			const assetTypes = Object.keys(this.subscriptions);
			for (let i = 0; i < assetTypes.length; i++) {
				const subAssetType = assetTypes[i];
				if (this.subscriptions[subAssetType].refId === refIdLocal) {
					assetTypeLocal = subAssetType;
					break;
				}
			}
		}

		const data = {
			Arguments: {
				Uics: this.subscriptions[assetTypeLocal].uics.join(','),
				AssetType: assetTypeLocal,
				FieldGroups: ['HistoricalChanges'],
				Amount: 1,
				AmountType: 'Quantity'
			},
			ContextId: this.contextId,
			ReferenceId: refIdLocal
		};
		const body = JSON.stringify(data);
		const onSuccess = text => {
			// here we can fill data for first subscription response, this might be same as the data received from server
			const res = JSON.parse(text);
			if (res.Snapshot) {
				this.checkData(res.Snapshot.Data);
			}
		};

		const headers = {
			Authorization: 'BEARER ' + this.accessToken,
			'Content-Type': 'application/json'
		};
		this.createRequest({
			url: this.priceSubscriptionUrl,
			method: 'POST',
			onSuccess,
			headers,
			body
		});
	}

	handleSubscribePrices() {
		Object.keys(this.subscriptions).forEach(assetType => {
			this.subscribeInfoPrice(this.subscriptions[assetType].refId, assetType);
		});
	}

	handleDisconnection(code, reason) {
		// Closing the connection by closing the socket
		this.connection.close(code || 1000, reason || 'Normal Closure');
	}

	parseMessages(data) {
		let index = 0;

		const messages = [];

		while (index < data.byteLength) {
			const message = new DataView(data);
			// First 8 bytes make up the message id. A 64 bit integer.
			const messageId = long.fromBytesLE(new Uint8Array(data, 0, 8)).toString();
			index += 8;
			// 2 bytes make up the reserved field.This field is reserved for future use and it should be ignored by the client.
			const reservedField = message.getInt16(index);
			index += 2;
			// 1 byte makes up the reference id length as an 8 bit integer. The reference id has a max length og 50 chars.
			const referenceIdSize = message.getInt8(index);
			index += 1;
			// n bytes make up the reference id. The reference id is an ASCII string.
			const referenceIdBuffer = new Int8Array(data.slice(index, index + referenceIdSize));
			const referenceId = String.fromCharCode(...referenceIdBuffer);
			// const referenceId = String.fromCharCode.apply(String, referenceIdBuffer);
			index += referenceIdSize;
			// 1 byte makes up the payload format. The value 0 indicates that the payload format is Json.
			const payloadFormat = message.getUint8(index);
			index++;
			// 4 bytes make up the payload length as a 32 bit integer.
			const payloadSize = message.getUint32(index, true);
			index += 4;
			// n bytes make up the actual payload. In the case of the payload format being Json, this is a UTF8 encoded string.
			const payloadBuffer = new Int8Array(data.slice(index, index + payloadSize));
			let payload;
			if (!payloadFormat) {
				payload = String.fromCharCode(...payloadBuffer);
				// payload = String.fromCharCode.apply(String, payloadBuffer);
				this.checkData(JSON.parse(payload));
			}

			index += payloadSize;

			messages[messages.length] = {
				messageId,
				reservedField,
				referenceId,
				payloadFormat,
				payload
			};
		}

		return messages;
	}

	// update content in both mobile and desktop views
	updateContent(row, type, value) {
		const decimalPoints = Number(row.dataset.decimal || 3) + 1;
		const precision = Number(row.dataset.precision || 1);
		const spreadVal =
			type === 'spread'
				? (value * 10 ** Number(row.dataset.decimal)).toFixed(precision)
				: value.toFixed(2);
		let localVal = type === 'ask' || type === 'bid' ? value.toFixed(decimalPoints) : spreadVal;
		const container = row.querySelector(`[data-price="${type}"]`);
		if (container && !Number.isNaN(localVal)) {
			const prevText = container.innerHTML;
			const prevValue = parseFloat(prevText) || 0; // default 0
			if (value !== prevValue) {
				localVal = this.applyStylingOnCellBasedOnValue(type, container, localVal, value, prevValue);

				if (type === 'mid' && prevText) {
					// extract currency
					const currency = prevText.trim().split(' ')[1];
					localVal += ' ' + currency;
				}
				container.innerHTML = localVal;
			}
			return parseFloat(container.innerHTML);
		}
		return Number.isNaN(localVal) ? value : localVal;
		// else no need to update
	}

	applyStylingOnCellBasedOnValue(type, container, localVal, value, prevValue) {
		let tempLocalVal = localVal;
		if (type === 'change') {
			const indicatorClass = container.parentElement.querySelector('.change-indicator').classList;
			const absVal = Math.abs(tempLocalVal).toFixed(2);
			if (value > prevValue) {
				indicatorClass.remove('change-indicator--negative');
				indicatorClass.add('change-indicator--positive');
				tempLocalVal = `+${absVal}%`;
			} else {
				indicatorClass.remove('change-indicator--positive');
				indicatorClass.add('change-indicator--negative');
				tempLocalVal = `-${absVal}%`;
			}
		} else if (type === 'spread') {
			// No change in color
		} else {
			container.style.color = value > prevValue ? 'green' : 'red';
		}
		return tempLocalVal;
	}

	update({ Bid, Ask, Mid }, Change, Uic) {
		const rows = Array.from(this.element.querySelectorAll(`[data-uic="${Uic}"]`));
		// For different Asset type we can have same uic but the data for them is same hence
		// we will update all the rows with same saame Uic
		if (rows.length) {
			rows.forEach(row => {
				let tempAsk = parseFloat(row.querySelector(`[data-price="ask"]`).innerHTML);
				let tempBid = parseFloat(row.querySelector(`[data-price="bid"]`).innerHTML);
				Ask && (tempAsk = this.updateContent(row, 'ask', Ask));
				Bid && (tempBid = this.updateContent(row, 'bid', Bid));
				Mid && this.updateContent(row, 'mid', Mid);
				this.updateContent(row, 'spread', tempAsk - tempBid);
				Change && this.updateContent(row, 'change', Change);
			});
		}
	}

	checkData(data) {
		// object is an array for infoprices endpoint
		const changeFrequency = this.changeCategories[this.historicalChange];
		if (data && data.length) {
			data.forEach(({ Quote, HistoricalChanges, Uic }) => {
				const change = HistoricalChanges && HistoricalChanges[changeFrequency];
				Quote && this.update(Quote, change, Uic);
			});
		}
	}

	createRequest({ url, method, headers, body, onSuccess, onError }) {
		const config = { method, headers };
		if (body) config.body = body;

		window
			.fetch(url, config)
			.then(response => {
				if (!response.ok) throw response.statusText;
				return response.text();
			})
			.then(text => {
				onSuccess && onSuccess(text);
			})
			.catch(e => {
				// eslint-disable-next-line no-console
				console.error(`Error in request : ${e}`);
				onError && onError(e);
			});
	}
}
